import { useMemo, useState } from 'react';
import { addDays, formatMinutes, startOfWeek } from '@otakotakdev/shared';
import { useWeeklyReview, useSettings } from '@/hooks/api';
import { Badge, EmptyState, ErrorState, ListSkeleton, PageHeader } from '@/components/ui';

export function ReviewPage() {
  const settingsQuery = useSettings();
  const timezone = settingsQuery.data?.settings.timezone ?? 'Asia/Jakarta';

  const todayLocal = useMemo(() => {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return fmt.format(new Date());
  }, [timezone]);

  const [weekStart, setWeekStart] = useState(() => startOfWeek(todayLocal));
  const isCurrentWeek = weekStart === startOfWeek(todayLocal);

  const { data, isLoading, isError, error, refetch } = useWeeklyReview(
    isCurrentWeek ? undefined : weekStart,
  );

  const shift = (delta: number) => setWeekStart((prev) => addDays(prev, delta * 7));

  if (isLoading) return <ListSkeleton rows={6} />;
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Failed to load review'}
        onRetry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  const maxMinutes = Math.max(1, ...data.activitiesByDay.map((d) => d.minutes));

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Weekly review"
        subtitle={`${data.weekStart} → ${data.weekEnd} · ${data.timezone}`}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className="btn-ghost" onClick={() => shift(-1)} aria-label="Previous week">
              ←
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setWeekStart(startOfWeek(todayLocal))}
              disabled={isCurrentWeek}
            >
              This week
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => shift(1)}
              disabled={isCurrentWeek}
              aria-label="Next week"
            >
              →
            </button>
          </div>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="card px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-muted">Goals completed</p>
          <p className="mt-1 text-lg font-semibold">{data.completedGoals.length}</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-muted">Activities done</p>
          <p className="mt-1 text-lg font-semibold">{data.completedActivities.length}</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-muted">Total time</p>
          <p className="mt-1 text-lg font-semibold">{formatMinutes(data.totalActivityMinutes)}</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-muted">Completion rate</p>
          <p className="mt-1 text-lg font-semibold">
            {data.completionRate.goals}% goals · {data.completionRate.activities}% activities
          </p>
        </div>
      </div>

      <section className="card mb-4 p-4" aria-labelledby="by-day">
        <h2 id="by-day" className="mb-3 text-sm font-semibold text-muted">
          Activity by day (minutes)
        </h2>
        <div
          className="flex h-36 items-end gap-2"
          role="img"
          aria-label="Bar chart of minutes logged per day this week"
        >
          {data.activitiesByDay.map((day) => (
            <div key={day.date} className="flex flex-1 flex-col items-center gap-1">
              <span className="text-xs text-muted">{day.minutes || ''}</span>
              <div
                className="w-full rounded-t bg-accent/70"
                style={{
                  height: `${Math.round((day.minutes / maxMinutes) * 100)}%`,
                  minHeight: day.minutes ? 4 : 2,
                }}
              />
              <span className="text-xs text-muted">{day.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="card p-4" aria-labelledby="completed-goals">
          <h2 id="completed-goals" className="mb-2 text-sm font-semibold text-muted">
            Goals completed
          </h2>
          {data.completedGoals.length === 0 ? (
            <p className="text-sm text-muted">No goals completed this week.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.completedGoals.map((g) => (
                <li key={g.id} className="flex items-center justify-between text-sm">
                  <span className="truncate">{g.title}</span>
                  <Badge tone="success">{g.completedAt?.slice(0, 10)}</Badge>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-4" aria-labelledby="unfinished">
          <h2 id="unfinished" className="mb-2 text-sm font-semibold text-muted">
            Unfinished goals
          </h2>
          {data.unfinishedGoals.length === 0 ? (
            <p className="text-sm text-muted">Nothing outstanding — excellent.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.unfinishedGoals.slice(0, 10).map((g) => (
                <li key={g.id} className="flex items-center justify-between text-sm">
                  <span className="truncate">{g.title}</span>
                  <Badge tone={g.priority === 'high' ? 'danger' : 'default'}>{g.priority}</Badge>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-4" aria-labelledby="top-tags">
          <h2 id="top-tags" className="mb-2 text-sm font-semibold text-muted">
            Most-used tags
          </h2>
          {data.topTags.length === 0 ? (
            <p className="text-sm text-muted">No tagged notes this week.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {data.topTags.map((t) => (
                <li key={t.name}>
                  <Badge>{`${t.name} · ${t.count}`}</Badge>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-4" aria-labelledby="mood">
          <h2 id="mood" className="mb-2 text-sm font-semibold text-muted">
            Check-ins
          </h2>
          {data.checkInSummary.length === 0 ? (
            <p className="text-sm text-muted">No check-ins recorded this week.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.checkInSummary.map((c) => (
                <li key={c.date} className="flex items-center justify-between text-sm">
                  <span>{c.date}</span>
                  <Badge
                    tone={
                      c.mood === 'good' ? 'success' : c.mood === 'difficult' ? 'danger' : 'default'
                    }
                  >
                    {c.mood ?? '—'}
                    {c.energy != null ? ` · ${c.energy}/5` : ''}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {data.completedActivities.length > 0 && (
        <section className="card mt-4 p-4" aria-labelledby="completed-activities">
          <h2 id="completed-activities" className="mb-2 text-sm font-semibold text-muted">
            Completed activities
          </h2>
          <ul className="divide-y divide-white/5">
            {data.completedActivities.slice(0, 20).map((a) => (
              <li key={a.id} className="flex items-center justify-between py-2 text-sm">
                <span className="truncate">{a.title}</span>
                <span className="shrink-0 text-xs text-muted">
                  {a.activityDate}
                  {a.durationMinutes != null ? ` · ${formatMinutes(a.durationMinutes)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.completedGoals.length === 0 &&
        data.completedActivities.length === 0 &&
        data.unfinishedGoals.length === 0 && (
          <EmptyState title="Quiet week" hint="No activity recorded for this period yet." />
        )}
    </div>
  );
}
